import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GripVertical, X, ExternalLink } from "lucide-react";

export interface TabData {
  id: string;
  url: string;
  title: string;
  description: string;
  thumbnail?: string;
}

interface TabTileProps {
  tab: TabData;
  onRemove: (id: string) => void;
  minimalistic?: boolean;
  editable?: boolean;
}

export function TabTile({ tab, onRemove, minimalistic = false, editable = true }: TabTileProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (minimalistic) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group ${isDragging ? "opacity-50 z-50" : ""}`}
      >
        <Card className="transition-all hover:shadow-md bg-card/50 backdrop-blur-sm">
          <div className="flex items-center gap-3 p-3">
            {editable && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 cursor-grab active:cursor-grabbing shrink-0"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </Button>
            )}

            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground text-sm line-clamp-1">
                {tab.title}
              </h3>
              <a
                href={tab.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-primary transition-colors line-clamp-1 flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                <span className="truncate">{tab.url}</span>
              </a>
            </div>

            {editable && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0 hover:bg-destructive/10 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove(tab.id);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group ${isDragging ? "opacity-50 z-50" : ""}`}
    >
      <Card className="overflow-hidden transition-all duration-500 hover:shadow-glossy hover:-translate-y-2 shadow-card bg-gradient-card shine-effect">
        {/* Thumbnail */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {tab.thumbnail ? (
            <img
              src={tab.thumbnail}
              alt={tab.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-primary">
              <ExternalLink className="w-12 h-12 text-primary-foreground opacity-50" />
            </div>
          )}
          
          {/* Actions Overlay */}
          {editable && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-start justify-between p-2">
              <Button
                size="icon"
                variant="secondary"
                className="h-8 w-8 cursor-grab active:cursor-grabbing"
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="destructive"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove(tab.id);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 space-y-2.5">
          <h3 className="font-semibold text-foreground line-clamp-1 text-base">
            {tab.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
            {tab.description}
          </p>
          <a
            href={tab.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:text-primary-glow transition-colors line-clamp-1 flex items-center gap-1.5 font-medium"
          >
            <ExternalLink className="h-3 w-3" />
            {tab.url}
          </a>
        </div>
      </Card>
    </div>
  );
}
